import { AnimatedHeight } from '@codezo/offer-extras';
import { Box, ChevronDown } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import type { RequestedProduct } from './types';
import { Card, MicroLabel, cn } from './ui/primitives';

/** Specifications on the face of the card before the rest fold away. Four is
 * what fits on one line of the spec grid at the width the middle column takes. */
const FACE_SPECS = 4;

/** Thumbnails beside the hero image — one row of three, the grid it sits in. */
const FACE_THUMBS = 3;

/**
 * The picture well. One frame for the hero and the thumbs, so a product with no
 * image still occupies the space its neighbours do and the row of cards keeps
 * its left edge.
 *
 * A button only when the app gave us somewhere to go: the images open in the
 * app's own viewer, and without one the picture is just a picture.
 */
function MediaFrame({
	children,
	square,
	onOpen,
	title,
}: {
	children?: ReactNode;
	square?: boolean;
	onOpen?: () => void;
	title?: string;
}) {
	const frame = cn(
		'flex w-full items-center justify-center overflow-hidden border border-line-2 bg-surface-fill text-text-4',
		square ? 'aspect-square rounded-[var(--r-1)]' : 'aspect-[4/3] rounded-[var(--r-2)]'
	);
	const body = children ?? <Box size={square ? 12 : 18} />;

	if (!onOpen) return <span className={frame}>{body}</span>;
	return (
		<button
			type="button"
			title={title}
			onClick={onOpen}
			className={cn(frame, 'p-0 transition-opacity hover:opacity-80')}
		>
			{body}
		</button>
	);
}

/** A quantity the client asked to have priced. */
function QtyChip({ children }: { children: ReactNode }) {
	return (
		<span className="rounded-[var(--r-1)] border border-line-1 bg-surface-fill px-2.5 py-px font-mono text-xs text-text-2">
			{children}
		</span>
	);
}

/**
 * What the client asked to have priced — one dossier card per product.
 *
 * ── Why not a table ──
 *
 * It was one: a row per product with the specifications, the description and the
 * files folded behind a disclosure. The columns lined up, and everything that
 * says WHAT WAS ASKED FOR — the picture, the print, the material, the tech pack —
 * was behind a click nobody made. A quote is read to check that what we are about
 * to price is the thing the client meant, and that check needs the dossier open.
 *
 * So: a card per product, everything on its face. Nothing is behind a disclosure
 * except the tail of a long specification list, and that only because a product
 * with sixteen specifications would push the next product off screen.
 *
 * ── The three columns ──
 *
 * Pictures, then identity, then terms. They are three flex items rather than a
 * grid because they have different appetites — the pictures want a fixed 200px,
 * the description wants everything left over, the terms column wants only as much
 * as its widest chip — and they wrap in that order as the card narrows.
 *
 * The terms column carries a left rule, which is the one thing that goes soft
 * when it wraps to its own line: the rule ends up under the description rather
 * than beside it. Accepted, because the alternative — a border that appears at a
 * viewport breakpoint — lies about a column that wraps on CONTAINER width, and
 * this section renders both beside a rail and full width.
 *
 * ── Everything drawn here is a prop ──
 *
 * The card face reads structured data (media, description, specs) rather than the
 * opaque panel the row used to expand, because the layout is now the design: the
 * apps could not lay their own node out in three columns without the card knowing
 * what was in it.
 *
 * Files stay a slot — the admin uploads, describes and deletes them, the client
 * only reads them, and that difference is the app's to own. What they must NOT
 * differ on is what a file looks like: both fill the slot with a `FileGrid` of
 * `FileTile`s, the same tile the product page lists its assets in.
 */
export function RequestedProducts({
	items,
	emptyState,
	openIds,
}: {
	items: RequestedProduct[];
	emptyState?: ReactNode;
	/** Products whose full specification list is unfolded on arrival. Seeded, not
	 * controlled: it opens them once and then leaves the reader alone. */
	openIds?: string[];
}) {
	const [expanded, setExpanded] = useState<Set<string>>(new Set());

	/* Keyed on the joined ids rather than the array: the caller rebuilds its item
	   list on every refetch, and re-running this would reopen a list the reader
	   had just folded away. */
	const openKey = (openIds ?? []).join(',');
	useEffect(() => {
		if (!openKey) return;
		setExpanded((prev) => new Set([...prev, ...openKey.split(',')]));
	}, [openKey]);

	const toggle = (id: string) =>
		setExpanded((prev) => {
			const next = new Set(prev);
			if (!next.delete(id)) next.add(id);
			return next;
		});

	if (items.length === 0) {
		return <Card>{emptyState}</Card>;
	}

	return (
		<div className="flex flex-col gap-3">
			{items.map((item) => {
				/* One list, whatever the caller had: a page that only ever had the
				   one thumbnail keeps working, it just gets a hero and no strip. */
				const media = item.media?.length
					? item.media
					: item.product.image
						? [item.product.image]
						: [];
				const thumbs = media.slice(1, 1 + FACE_THUMBS);
				const specs = item.specs ?? [];
				const isExpanded = expanded.has(item.id);
				const shown = isExpanded ? specs : specs.slice(0, FACE_SPECS);

				return (
					<Card key={item.id}>
						<div className="flex flex-wrap gap-5 p-4">
							<div className="flex min-w-[150px] flex-[0_1_200px] flex-col gap-1.5">
								<MediaFrame
									onOpen={
										item.onOpenImages ? () => item.onOpenImages?.(0) : undefined
									}
									title={`View images of ${item.product.name}`}
								>
									{media[0]}
								</MediaFrame>
								{thumbs.length > 0 && (
									<div className="grid grid-cols-3 gap-1.5">
										{thumbs.map((thumb, i) => (
											<MediaFrame
												key={i}
												square
												onOpen={
													item.onOpenImages
														? () => item.onOpenImages?.(i + 1)
														: undefined
												}
												title={`View images of ${item.product.name}`}
											>
												{thumb}
											</MediaFrame>
										))}
									</div>
								)}
							</div>

							<div className="flex min-w-[260px] flex-[2_1_300px] flex-col gap-2.5">
								<div>
									<div className="flex flex-wrap items-baseline gap-2.5">
										<span className="text-base font-medium text-text-1">
											{item.product.name}
										</span>
										{item.product.sku && (
											<span className="font-mono text-xs text-text-4">
												{item.product.sku}
											</span>
										)}
									</div>
									{item.description && (
										<div className="mt-1.5 max-w-[520px] text-sm leading-relaxed text-text-3">
											{item.description}
										</div>
									)}
								</div>

								{specs.length > 0 && (
									<div className="flex flex-col gap-2">
										{/* Measured rather than switched: the tail unfolds into
										    the same grid, so the four specifications already on
										    the card don't jump as the rest arrive. */}
										<AnimatedHeight step={isExpanded ? 'all' : 'face'}>
											<div className="grid gap-x-6 gap-y-1.5 [grid-template-columns:repeat(auto-fit,minmax(150px,1fr))]">
												{shown.map(([label, value], i) => (
													<div
														key={`${label}-${i}`}
														className="flex min-w-0 flex-col"
													>
														<span className="text-2xs text-text-4">
															{label}
														</span>
														{/* Two lines while the list is short, all of
														    them once the reader has asked for all of
														    them — which they did to read them.
														    Clipping to one line lost the tail of every
														    material and print spec, which is the half
														    that says what was asked for. */}
														<span
															className={cn(
																'min-w-0 text-sm break-words text-text-2',
																!isExpanded && 'line-clamp-2'
															)}
														>
															{value}
														</span>
													</div>
												))}
											</div>
										</AnimatedHeight>

										{specs.length > FACE_SPECS && (
											<button
												type="button"
												onClick={() => toggle(item.id)}
												aria-expanded={isExpanded}
												className="flex w-fit items-center gap-1 bg-transparent p-0 text-xs text-text-4 transition-colors hover:text-text-1"
											>
												<ChevronDown
													size={12}
													className={cn(
														'transition-transform duration-150',
														isExpanded && 'rotate-180'
													)}
												/>
												{isExpanded
													? 'Fewer specifications'
													: `All ${specs.length} specifications`}
											</button>
										)}
									</div>
								)}

								{item.note && (
									<div className="text-2xs leading-normal text-text-4">
										{item.note}
									</div>
								)}
							</div>

							<div className="flex min-w-[170px] flex-[1_0_180px] flex-col gap-3 border-l border-line-1 pl-5">
								<div>
									{/* The row menu rides the first label rather than floating
									    over the card: the terms column is where this product is
									    edited, and a menu with nothing beside it reads as
									    belonging to the section, not the product. */}
									<div
										className={cn(
											'mb-1.5 flex items-center justify-between gap-2',
											/* Only when there is a menu: the row takes the
											   button's height so the label lines up with it,
											   and without one that would be dead space over
											   the quantities. */
											item.actions && 'min-h-[28px]'
										)}
									>
										<MicroLabel>Quantities</MicroLabel>
										{item.actions && (
											<span className="shrink-0">{item.actions}</span>
										)}
									</div>
									<div className="flex flex-wrap gap-1.5">
										{item.quantities.length === 0 ? (
											<span className="text-sm text-text-4">—</span>
										) : (
											item.quantities.map((quantity) => (
												<QtyChip key={quantity}>{quantity}</QtyChip>
											))
										)}
									</div>
								</div>

								<div>
									<MicroLabel className="mb-1 block">Deadline</MicroLabel>
									<span className="font-mono text-sm text-text-2">
										{item.deadline || '—'}
									</span>
								</div>

								{item.primaryAction && (
									<div className="mt-auto pt-1">{item.primaryAction}</div>
								)}
							</div>
						</div>

						{/* Full width under the columns. Files are tiles now, not a strip
						    of chips, and a tile grid in the middle column would be three
						    tiles wide with the description clipped in each — which is the
						    half of a quote file that says what it is for. */}
						{item.files && (
							<div className="border-t border-line-1 px-4 py-3.5">{item.files}</div>
						)}
					</Card>
				);
			})}
		</div>
	);
}
